import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'uinix-ui';

import { TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT } from '~/common/constants/max_lengths';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { Nullable } from '~/eds/src/types/Nullable';
import { PilotId } from '~/eds/src/types/PilotId';
import { EnumValue } from '~/evifields/src/types/Value';
import { useMaxHeightBasedOnDistance } from '~/hooks/useMaxHeightBasedOnDistance';

type Props = {
  fieldId: PilotId;
  values: EnumValue[];
  onChange: (values: EnumValue[]) => void;
};
export const TextDelimitedEnumSetValue = ({
  values: initialValues,
  onChange,
}: Props) => {
  const intl = useIntl();
  const theme = useTheme();
  const maxHUpperLimit = theme.sizes['textarea.max-height'] as number;

  const [debouncedState, values, setValues] = useDebouncedState<
    Nullable<string>
  >(initialValues.join('\n'));
  const [ref, maxH] = useMaxHeightBasedOnDistance<HTMLDivElement>({
    currentMaxHeight: maxHUpperLimit,
  });

  const isLimitExceeded = useMemo(() => {
    if (!debouncedState) return false;
    return (
      debouncedState.split('\n').length >
      TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT
    );
  }, [debouncedState]);

  const handleChange = (updatedValues: Nullable<string>) => {
    setValues(updatedValues);
    if (updatedValues) {
      const valuesArray = updatedValues
        .split('\n')
        .map((value) => value.trim())
        .filter((value) => !!value);

      onChange(valuesArray);
    } else {
      onChange([]);
    }
  };

  return (
    <Layout variant="menu.container" w={250} direction="column">
      <div ref={ref}>
        <TextArea
          name="filter-values"
          value={values}
          onChange={handleChange}
          placeholder={intl.formatMessage({
            id: 'Filters.TextDelimitedEnumSetValue.placeholderEnterValues',
            defaultMessage: 'Enter values',
            description:
              'Placeholder text for a textarea where users can enter multiple values.',
          })}
          rows={6}
          maxH={maxH}
        />
      </div>
      {isLimitExceeded ? (
        <Text preset="error" variant="tiny-bold">
          {intl.formatMessage(
            {
              id: 'Filters.TextDelimitedEnumSetValue.maximumValuesExceeded',
              defaultMessage:
                'Maximum {valueLimit} values. Remove additional values to search',
              description:
                'Error message below a text area when the limit of entered values is exceeded. Example: valueLimit = 100',
            },
            { valueLimit: TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT },
          )}
        </Text>
      ) : (
        <Text preset="description">
          {intl.formatMessage(
            {
              id: 'Filters.TextDelimitedEnumSetValue.enterValues',
              defaultMessage:
                'Paste up to {valueLimit} line-separated values. Press Shift + Return to add a new line.',
              description:
                'Textarea label that explains how many values can be entered, and how to add a new line. Example: valueLimit = 100',
            },
            { valueLimit: TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT },
          )}
        </Text>
      )}
    </Layout>
  );
};
